import { addressSchema } from '@commenda/schema/address';
import { NonTimezoneSensitiveDate } from '@commenda/utils';
import { LegacyDocumentType } from '@prisma/client';
import { isValidPhoneNumber } from 'libphonenumber-js';
import * as z from 'zod';

export const companyInfoSchema = z
  .object({
    companyLegalName: z
      .string()
      .trim()
      .min(1, { message: 'Please enter a company name.' }),
    companyLegalName2: z
      .string()
      .trim()
      .min(1, { message: 'Please enter a company name.' }),
    companyLegalName3: z
      .string()
      .trim()
      .min(1, { message: 'Please enter a company name.' }),
    // companyNameSuffix: z.string().trim(),
    companyRegisteredEmailAddress: z
      .string()
      .trim()
      .email({ message: 'Please enter an email address.' })
      .transform((val) => val.toLowerCase()),
    hasOptedForUKMailingAddress: z
      .boolean()
      .nullable()
      .refine((data) => data !== null, {
        message: 'Please select an option',
      }),
    registrationLocation: z.string().trim().nullable(),
    mailingAddress: addressSchema.optional(),
    principalPlaceOfBusiness: z.string().nullable().optional(),
    operatingAddress: addressSchema.optional(),
    isConvertingToLimitedCompany: z
      .boolean()
      .nullable()
      .refine((data) => data !== null, {
        message: 'Please select an option',
      }),
    convertingToLimitedCompanyName: z.string().trim().optional(),
    hasCT16Payments: z
      .boolean()
      .nullable()
      .refine((data) => data !== null, {
        message: 'Please select an option',
      }),
    hasNoSuchStartSellDate: z.boolean().nullable(),
    startDateSameAsSetUp: z.boolean().nullable(),
    hasDifferentDateInTheFuture: z.boolean().nullable(),
    companyStartBuySellDate: z.string().trim().nullable(),
    businessDescription: z.string().trim().min(25, {
      message: 'Please enter a description with at least 25 characters.',
    }),
  })
  .superRefine((data, ctx) => {
    if (
      data.hasNoSuchStartSellDate === null &&
      data.startDateSameAsSetUp === null &&
      data.hasDifferentDateInTheFuture === null
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please select an option',
        path: ['hasNoSuchStartSellDate'],
      });
    }

    if (
      data.hasDifferentDateInTheFuture === true &&
      !data.companyStartBuySellDate
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please select a date when your company will start trading',
        path: ['companyStartBuySellDate'],
      });
    }

    if (data.isConvertingToLimitedCompany) {
      if (
        !data.convertingToLimitedCompanyName ||
        data.convertingToLimitedCompanyName.trim() === ''
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please provide the name of the business being converted',
          path: ['convertingToLimitedCompanyName'],
        });
      }
    }
  });

export const directorSchema = z.object({
  directorFirstName: z
    .string()
    .trim()
    .min(1, { message: 'Please enter a valid first name' }),
  directorLastName: z.string().trim().nullable(),
  number: z.string().refine((value) => isValidPhoneNumber(value), {
    message: 'Please enter a valid phone number',
  }),
  email: z.string().email({ message: 'Please enter a valid email address' }),
  dateOfBirth: z
    .string()
    .min(1, { message: 'Please enter a valid date of birth' })
    .refine(
      (value) => {
        if (!value) return false;
        const currentDate = NonTimezoneSensitiveDate.fromNativeDate(new Date());
        const nonTimeZoneSensitiveDate =
          NonTimezoneSensitiveDate.fromNativeDate(new Date(value));
        return currentDate.gte(nonTimeZoneSensitiveDate);
      },
      { message: 'Date of birth must not be in the future.' }
    ),
  personalDetails: z.object({
    nationality: z.string().min(1, { message: 'Please select nationality' }),
    educationalQualification: z
      .string()
      .trim()
      .min(1, { message: 'Please select educational qualification' })
      .optional(),
  }),
  passportProof: z.object({
    id: z.number().optional(),
    documentType: z.nativeEnum(LegacyDocumentType),
    fileName: z.string(),
    fileUrl: z.string(),
  }),
  operatingAddress: addressSchema,
  corporationHasDirector: z
    .array(
      z.object({
        corporationId: z.number(),
        // Null on 68 of production's 1,372 links: the column is Json? and nothing requires it.
        // A director is judged on their own details, so a link that records nothing is not a gap
        // in them — and the list carries links to other companies' entities too.
        additionalDirectorInformation: z
          .object({
            hasSignificantControl: z.boolean().optional(),
            isAuthorizedSignatory: z.boolean().optional(),
          })
          .nullable(),
      })
    )
    .optional(),
  incorporationInfo: z
    .object({
      hasUsedDifferentName: z
        .boolean()
        .nullable()
        .refine((data) => data !== null, {
          message: 'Please select an option',
        }),
      jobTitle: z
        .string()
        .trim()
        .min(1, { message: 'Please enter a job title' }),
    })
    .optional(),
});

export type CompanyInfoFormFields = z.infer<typeof companyInfoSchema>;
export type DirectorFormFields = z.infer<typeof directorSchema>;
